import { ENDPOINT } from '~/constants/endpoints';

import { type UUID } from '~/types/common';

import { HTTPError, vestigasApi } from '~/services/kyClient';
import ToastService from '~/services/toast.service';

import { i18n, log } from '~/utils';

import { zodSupplierLetterMetaSchema } from '../validations';

import { type SupplierLetterMeta } from './types';

/**
 * Fetches the supplier authorization letter for a CompanyAccount.
 * Returns `null` when no letter has been configured (404).
 *
 * @param companyAccountId - The ID of the company account.
 * @returns The supplier letter meta, or null when none is configured.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin-Supplier-Letter/operation/get_company_account_supplier_letter_vadmin_company_accounts__company_account_id__supplier_letter_get
 */
export const fetchCompanyAccountSupplierLetter = async (
  companyAccountId: UUID,
): Promise<SupplierLetterMeta | null> => {
  try {
    return await vestigasApi
      .get(ENDPOINT.COMPANY.ACCOUNT.SUPPLIER_LETTER.GET(companyAccountId), {
        validate: {
          // Response carries a time-limited SAS download URL; keep it out of
          // Sentry breadcrumbs/logs.
          isResponseSensitive: true,
          response: zodSupplierLetterMetaSchema,
        },
      })
      .json<SupplierLetterMeta>();
  } catch (error) {
    if (error instanceof HTTPError && error.response.status === 404) {
      return null;
    }

    log.error({
      message: 'Error fetching company account supplier letter',
      error,
    });

    ToastService.error(
      i18n.t(
        ($) => $.companyAccount.fetchCompanyAccountSupplierLetter.error.generic,
        { ns: 'data' },
      ),
    );

    throw error;
  }
};
